import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useAuth } from "../../services/AuthContext";
import { normalizeLanguage } from "../../services/language";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import "../../components/lobby/lobbyModern.css";
import "./premium.css";

// Membresía Premium: 2 USD al mes por PayPal (la misma cuenta que «Haz una contribución»
// de Caissa). Con las claves de PayPal en el servidor (GET /premium/status → configured)
// es una suscripción: se paga en PayPal y Premium se enciende solo en la cuenta que pagó.
// Sin claves queda el pago a mano (paypal.me + avisar, y el administrador lo activa).
export const PREMIUM_PRICE_USD = 2;
export const PREMIUM_PAYPAL_URL = `https://paypal.me/muinoxima/${PREMIUM_PRICE_USD}USD`;
// Donación libre: la misma cuenta de PayPal, sin cantidad fija.
export const DONATE_PAYPAL_URL = "https://paypal.me/muinoxima";
export const PREMIUM_SUPPORT = { mail: "info@juegoxima.com", wa: "18647536014", waShow: "+1 864 753 6014" };

const BENEFITS = [
  ["🧠", "Análisis sin límite", "Analiza todas las partidas que quieras con el bot. Sin Premium tienes 3 análisis nuevos al día."],
  [
    "🎬",
    "Videos sin límite",
    "Descarga en video MP4 todas tus partidas, sin límites, desde tu computadora. El video se crea en tu propio equipo, así sale al momento y sin esperas. Sin Premium, 1 video al día.",
  ],
  ["💚", "Apoyas a XIMA", "Tu membresía nos ayuda a seguir creando juegos, torneos y mejoras para todos."],
];

const STEPS = [
  ["Paga con PayPal", `Pulsa «Pagar con PayPal» y envía ${PREMIUM_PRICE_USD} USD (puedes pagar con tarjeta, no hace falta cuenta).`],
  ["Avísanos", "Escríbenos por WhatsApp o correo con tu nombre de usuario de XIMA."],
  ["Listo", "Activamos tu Premium en tu cuenta. Cada mes que pagues, sigues siendo Premium."],
];

const AUTO_STEPS = [
  ["Pulsa «Pagar con PayPal»", "Te llevamos a PayPal. Puedes pagar con tu cuenta de PayPal o con tarjeta."],
  ["Confirma el pago", `Son ${PREMIUM_PRICE_USD} USD al mes. PayPal lo cobra solo cada mes y puedes cancelar cuando quieras.`],
  ["Listo", "Al volver a XIMA ya eres Premium en esta cuenta, sin esperar a nadie."],
];

const formatDay = (value) => {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime()) || !date.getTime()) return "";
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
};

const Premium = () => {
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language);
  const { user } = useAuth() || {};
  const routeActions = useModernRouteActions();
  const [access, setAccess] = useState(null);
  const [membership, setMembership] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadAccess = useCallback(() => {
    axios
      .get("/replays/access")
      .then((response) => setAccess(response?.data?.access || null))
      .catch(() => {});
  }, []);
  const loadMembership = useCallback(() => {
    axios
      .get("/premium/status")
      .then((response) => setMembership(response?.data || null))
      .catch(() => {});
  }, []);

  // Vuelta de PayPal: ?paypal=ok&subscription_id=… (o ?paypal=cancel).
  useEffect(() => {
    const result = searchParams.get("paypal");
    if (!result) return undefined;
    const subscriptionId = searchParams.get("subscription_id") || "";
    const next = new URLSearchParams(searchParams);
    ["paypal", "subscription_id", "ba_token", "token"].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
    if (result !== "ok" && result !== "active") {
      setNotice({ tone: "info", text: "No se hizo ningún pago. Puedes intentarlo cuando quieras." });
      return undefined;
    }
    let cancelled = false;
    setNotice({ tone: "info", text: "Confirmando tu pago con PayPal…" });
    const confirm = async (attempt) => {
      try {
        const response = await axios.post("/premium/paypal/confirm", { subscriptionId });
        if (cancelled) return;
        setMembership(response?.data || null);
        if (response?.data?.premium) {
          setNotice({ tone: "ok", text: "⭐ ¡Listo! Ya eres Premium. Gracias por apoyar a XIMA." });
          loadAccess();
          return;
        }
      } catch (_error) {
        // se reintenta abajo
      }
      if (cancelled) return;
      if (attempt < 6) {
        setTimeout(() => confirm(attempt + 1), 5000);
      } else {
        setNotice({
          tone: "info",
          text: "PayPal aún está procesando tu pago. En cuanto lo confirme, Premium se activa solo en tu cuenta.",
        });
      }
    };
    confirm(1);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Igual que el Historial: se esconde el menú antiguo y queda solo el del diseño Clásico.
  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active");
    return () => document.body.classList.remove("xima-lobby-modern-active");
  }, []);

  useEffect(() => {
    loadAccess();
    loadMembership();
  }, [loadAccess, loadMembership]);

  const startPayment = async () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const response = await axios.post("/premium/paypal/subscribe");
      const url = response?.data?.approveUrl;
      if (!url) throw new Error("sin enlace");
      window.location.assign(url);
    } catch (error) {
      setBusy(false);
      setNotice({
        tone: "error",
        text: error?.response?.data?.message || "No se pudo abrir PayPal. Inténtalo de nuevo en un momento.",
      });
    }
  };

  const cancelPayment = async () => {
    if (busy) return;
    const until = formatDay(membership?.until);
    const question = `¿Cancelar tu suscripción Premium? PayPal no te cobrará más${
      until ? ` y seguirás siendo Premium hasta el ${until}` : ""
    }.`;
    if (!window.confirm(question)) return;
    setBusy(true);
    try {
      const response = await axios.post("/premium/paypal/cancel");
      setMembership(response?.data || null);
      setNotice({ tone: "info", text: "Suscripción cancelada. PayPal ya no te cobrará más." });
    } catch (error) {
      setNotice({ tone: "error", text: error?.response?.data?.message || "No se pudo cancelar. Inténtalo de nuevo." });
    } finally {
      setBusy(false);
    }
  };

  const userName = user?.username || user?.loginIdentifier || user?.email || "";
  const note = `Hola, pagué la membresía Premium de XIMA (${PREMIUM_PRICE_USD} USD/mes). Mi usuario es: ${userName}`;
  const waUrl = `https://wa.me/${PREMIUM_SUPPORT.wa}?text=${encodeURIComponent(note)}`;
  const mailUrl = `mailto:${PREMIUM_SUPPORT.mail}?subject=${encodeURIComponent("Membresía Premium XIMA")}&body=${encodeURIComponent(note)}`;
  const isPremium = Boolean(membership?.premium || access?.premium);
  const automatic = Boolean(membership?.configured);
  const paidWithPaypal = membership?.source === "paypal" && Boolean(membership?.premium);
  const renews = paidWithPaypal && membership?.subscriptionStatus === "ACTIVE";
  const untilText = formatDay(membership?.until);
  const steps = automatic ? AUTO_STEPS : STEPS;

  return (
    <div className="xlm-root xpm-root">
      <ModernSidebar
        language={language}
        active="premium"
        badges={outletContext.sidebarBadges || {}}
        isAdmin={Boolean(outletContext.hasAdminMenuAccess)}
        actions={routeActions}
      />
      <div className="xlm-main">
        <header className="xlm-topbar">
          <div className="xlm-heading">
            <span>Más análisis, más videos y apoyo a XIMA</span>
            <h1>💎 Premium</h1>
          </div>
        </header>

        <section className="xpm-hero">
          <div className="xpm-price">
            <b>{PREMIUM_PRICE_USD} USD</b>
            <span>al mes</span>
          </div>
          <div className="xpm-hero-text">
            {isPremium ? (
              <>
                <strong className="xpm-badge">⭐ Ya eres Premium</strong>
                <p>
                  Tienes análisis y videos sin límite. ¡Gracias por apoyar a XIMA!
                  {paidWithPaypal && untilText
                    ? renews
                      ? ` Tu suscripción de PayPal se renueva sola cada mes.`
                      : ` Tu Premium dura hasta el ${untilText} y no se renovará.`
                    : ""}
                </p>
              </>
            ) : (
              <>
                <strong>Hazte miembro Premium</strong>
                <p>
                  {access && Number.isFinite(Number(access.remaining))
                    ? `Hoy te quedan ${access.remaining} análisis nuevos y ${Number(access.videosRemaining) || 0} ${
                        Number(access.videosRemaining) === 1 ? "video" : "videos"
                      } gratis.`
                    : "Analiza y descarga tus partidas sin límites."}
                </p>
              </>
            )}
            {automatic ? (
              membership?.guest ? (
                <p className="xpm-note">Crea una cuenta en XIMA para hacerte Premium.</p>
              ) : renews ? (
                <button type="button" className="xpm-cancel" onClick={cancelPayment} disabled={busy}>
                  Cancelar suscripción
                </button>
              ) : isPremium && !paidWithPaypal ? null : (
                <button type="button" className="xpm-pay" onClick={startPayment} disabled={busy}>
                  {busy ? "Abriendo PayPal…" : `Pagar ${PREMIUM_PRICE_USD} USD al mes con PayPal`}
                </button>
              )
            ) : (
              <a className="xpm-pay" href={PREMIUM_PAYPAL_URL} target="_blank" rel="noopener noreferrer">
                {isPremium ? "Pagar el próximo mes con PayPal" : `Pagar ${PREMIUM_PRICE_USD} USD con PayPal`}
              </a>
            )}
            {notice ? <p className={`xpm-notice is-${notice.tone}`}>{notice.text}</p> : null}
          </div>
          <a className="xpm-donate" href={DONATE_PAYPAL_URL} target="_blank" rel="noopener noreferrer">
            <span aria-hidden="true">❤️</span>
            Donar
          </a>
        </section>

        <section className="xpm-benefits">
          {BENEFITS.map(([icon, title, text]) => (
            <div className="xpm-card" key={title}>
              <span className="xpm-icon" aria-hidden="true">{icon}</span>
              <strong>{title}</strong>
              <p>{text}</p>
            </div>
          ))}
        </section>

        <section className="xpm-card xpm-steps">
          <strong>Cómo activarlo</strong>
          <ol>
            {steps.map(([title, text]) => (
              <li key={title}>
                <b>{title}.</b> {text}
              </li>
            ))}
          </ol>
          {automatic ? <p className="xpm-user">¿Dudas o algún problema con el pago? Escríbenos:</p> : null}
          <div className="xpm-contact">
            <a className="xpm-btn" href={waUrl} target="_blank" rel="noopener noreferrer">
              💬 WhatsApp {PREMIUM_SUPPORT.waShow}
            </a>
            <a className="xpm-btn" href={mailUrl}>
              ✉️ {PREMIUM_SUPPORT.mail}
            </a>
          </div>
          {userName ? (
            <p className="xpm-user">
              Tu usuario: <b>{userName}</b>
            </p>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default Premium;
